import React, { useEffect, useState } from 'react';
import { useGasI18n } from '../../shared/kit';
import { formatPrice } from '../shop-shared/format';
import { getOrderConfirmation } from './api';
import type { OrderConfirmation, ShopMerciInitData } from './types';

interface AppProps {
    initData: ShopMerciInitData;
}

export default function App({ initData }: AppProps) {
    const { t } = useGasI18n();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<OrderConfirmation | null>(null);

    useEffect(() => {
        let active = true;
        getOrderConfirmation(initData)
            .then((res) => { if (active) setData(res); })
            .catch(() => {
                if (active) {
                    setData({
                        state: initData.session_id ? 'processing' : 'not_found',
                        order: null,
                        urls: { user_home: '', agenda: '', shop: '' },
                    });
                }
            })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [initData]);

    if (loading) {
        return <p className="gas-vue-loading">{t('shop.cart.loading')}</p>;
    }

    if (!data) {
        return null;
    }

    const urls = data.urls;

    if (data.state === 'processing') {
        return (
            <div className="gas-merci-processing">
                <div className="gas-processing-icon">⏳</div>
                <h1>{t('shop.merci.processing_title')}</h1>
                <p>{t('shop.merci.processing_intro')}</p>
                <p>{t('shop.merci.processing_email')}</p>
                <div className="gas-processing-info">
                    <p><strong>{t('shop.merci.processing_whatnext')}</strong></p>
                    <ul>
                        <li>✅ {t('shop.merci.processing_step_paid')}</li>
                        <li>⏳ {t('shop.merci.processing_step_creating')}</li>
                        <li>📧 {t('shop.merci.processing_step_email')}</li>
                    </ul>
                    <p className="gas-merci-processing-hint"><small>{t('shop.merci.processing_refresh_hint')}</small></p>
                </div>
                <div className="gas-processing-actions">
                    <button type="button" onClick={() => window.location.reload()} className="gas-action-button gas-button-primary">
                        {t('shop.merci.refresh_page')}
                    </button>
                    <a href={urls.user_home} className="gas-action-button gas-button-secondary">{t('shop.merci.my_orders')}</a>
                </div>
            </div>
        );
    }

    if (data.state === 'not_found' || !data.order) {
        return (
            <div className="gas-merci-error">
                <h1>{t('shop.merci.not_found_title')}</h1>
                <p>{t('shop.merci.not_found_intro')}</p>
                <p>{t('shop.merci.not_found_contact')}</p>
                <div className="gas-error-actions">
                    <a href={urls.shop} className="gas-action-button gas-button-primary">{t('shop.merci.back_to_shop')}</a>
                    {urls.user_home && (
                        <a href={urls.user_home} className="gas-action-button gas-button-secondary">{t('shop.merci.my_orders')}</a>
                    )}
                </div>
            </div>
        );
    }

    const order = data.order;
    const pm = order.payment_method;
    const taxQuote = order.tax_quote;
    const showTax = Boolean(taxQuote?.enabled) && Number(order.tax_total || 0) > 0;
    const taxLabel = taxQuote?.prices_include_tax
        ? t('shop.tax.vat_included_rate', { rate: taxQuote.rate })
        : t('shop.tax.vat_rate', { rate: taxQuote?.rate || 0 });

    return (
        <div className="gas-merci-success">
            <div className="gas-success-icon">✓</div>
            <h1>{t('shop.merci.success_title')}</h1>
            <p className="gas-shop-order-confirmation">{t('shop.merci.order_confirmed', { id: order.id })}</p>
            {order.total > 0 && order.invoice_url && (
                <p>🧾 <a href={order.invoice_url}>{t('shop.merci.download_invoice')}</a></p>
            )}

            <div className="gas-order-summary">
                <h2>{t('shop.merci.order_summary')}</h2>
                <div className="gas-order-items">
                    {order.items.map((item, idx) => (
                        <div key={idx} className="gas-order-item">
                            <div className="gas-item-details">
                                <strong>
                                    <a href={item.product_url || '#'} className="gas-product-link">{item.product_title}</a>
                                </strong>
                                {item.variante ? (
                                    <span className="gas-variant-info">{item.variante.icon} {item.variante.nom}</span>
                                ) : item.variante_id !== 'default' ? (
                                    <span className="gas-variant-info">({item.variante_id})</span>
                                ) : null}
                                <span className="gas-quantity">× {item.quantity}</span>
                            </div>
                            <div className="gas-item-price">{formatPrice(item.display_total_price)}</div>
                        </div>
                    ))}
                </div>

                <div className="gas-order-total">
                    {order.has_coupon && (
                        <>
                            <div className="gas-order-total-line">
                                <span>{t('shop.checkout.subtotal')}</span>
                                <strong>{formatPrice(order.subtotal)}</strong>
                            </div>
                            <div className="gas-order-total-line gas-order-total-line-discount">
                                <span>
                                    {t('shop.cart.coupon_discount')}
                                    {order.coupon_code && <small> {order.coupon_code}</small>}
                                </span>
                                <strong>-{formatPrice(order.coupon_discount_total)}</strong>
                            </div>
                        </>
                    )}
                    {Number(order.shipping_total || 0) > 0 && (
                        <div className="gas-order-total-line">
                            <span>{t('shop.shipping.delivery')}</span>
                            <strong>{formatPrice(order.shipping_total)}</strong>
                        </div>
                    )}
                    {showTax && (
                        <div className="gas-order-total-line">
                            <span>{taxLabel}</span>
                            <strong>{formatPrice(order.tax_total)}</strong>
                        </div>
                    )}
                    <div className="gas-order-total-line gas-order-total-line-final">
                        <span>{t('shop.checkout.total')}</span>
                        <strong>{formatPrice(order.total)}</strong>
                    </div>
                </div>
            </div>

            {pm === 'free' && (
                <div className="gas-payment-info gas-payment-free">
                    <h3>🎉 {t('shop.merci.free_title')}</h3>
                    <p>{t('shop.merci.free_registered')}</p>
                    <p>{t('shop.merci.free_email')}</p>
                </div>
            )}
            {pm === 'bulletin' && (
                <div className="gas-payment-info gas-payment-bulletin">
                    <h3>💳 {t('shop.merci.bulletin_title')}</h3>
                    <p>{t('shop.merci.bulletin_intro')}</p>
                    <p><strong>{t('shop.merci.bulletin_important_label')}</strong> {t('shop.merci.bulletin_important')}</p>
                    <div className="gas-payment-details">
                        <p><strong>{t('shop.merci.amount_label')}</strong> {formatPrice(order.total)}</p>
                        <p><strong>{t('shop.merci.reference_label')}</strong> {t('shop.merci.order_ref', { id: order.id })}</p>
                    </div>
                </div>
            )}
            {pm === 'card' && (
                <div className="gas-payment-info gas-payment-card">
                    <h3>💳 {t('shop.merci.card_title')}</h3>
                    <p>{t('shop.merci.card_processed')}</p>
                    <p>{t('shop.merci.card_email')}</p>
                </div>
            )}

            <div className="gas-next-steps">
                <h3>{t('shop.merci.next_steps_title')}</h3>
                <ul>
                    <li>📧 {t('shop.merci.next_step_confirmation')}</li>
                    <li>📅 {t('shop.merci.next_step_access')}</li>
                    {initData.contact_email && (
                        <li>💬 {t('shop.merci.next_step_contact')} <a href={`mailto:${initData.contact_email}`}>{initData.contact_email}</a></li>
                    )}
                </ul>
            </div>

            <div className="gas-merci-actions">
                <a href={urls.user_home} className="gas-action-button gas-button-primary">{t('shop.merci.view_my_orders')}</a>
                <a href={urls.agenda} className="gas-action-button gas-button-secondary">{t('shop.merci.continue_shopping')}</a>
            </div>
        </div>
    );
}
